import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AWARD_RULE_TEXT } from '../lib/awards';
import { AwardBadge } from '../components/AwardBadge';
import { 
  Trophy, Clock, Users, Calendar, ArrowLeft, Eye, Heart,
  User, Award, CheckCircle, AlertCircle, BarChart3, Megaphone, PenLine, Gavel, Trash2, Pencil, Lock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ChallengeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [myStories, setMyStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Announce results state
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [awardAssignments, setAwardAssignments] = useState({});
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [announcing, setAnnouncing] = useState(false);
  const [judgeDiscussion, setJudgeDiscussion] = useState([]);
  
  // Cleanup state
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Judge management state (admin only)
  const [judgeSearch, setJudgeSearch] = useState('');
  const [judgeSearchResults, setJudgeSearchResults] = useState([]);
  const [currentJudges, setCurrentJudges] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const challengeUrl = user 
          ? `/challenges/${id}?user_id=${user.id}` 
          : `/challenges/${id}`;
        const challengeRes = await api.get(challengeUrl);
        const challengeData = challengeRes.data;
        
        // Compute status client-side (same as list page) to avoid mismatch
        const now = new Date().toISOString();
        if (challengeData.results_announced) {
          challengeData.status = 'completed';
        } else if (challengeData.start_date <= now && now < challengeData.end_date) {
          challengeData.status = 'active';
        } else if (challengeData.end_date <= now && now < challengeData.judging_end_date) {
          challengeData.status = 'judging';
        } else if (now >= challengeData.judging_end_date) {
          challengeData.status = 'completed';
        } else {
          challengeData.status = 'upcoming';
        }
        
        setChallenge(challengeData);

        // Fetch judge details for admin management
        if (user?.role === 'admin' && challengeData.judge_ids?.length > 0) {
          try {
            const judgeDetails = [];
            for (const jid of challengeData.judge_ids) {
              const res = await api.get(`/users/${jid}/profile`);
              judgeDetails.push({ id: jid, display_name: res.data.display_name, username: res.data.username });
            }
            setCurrentJudges(judgeDetails);
          } catch { setCurrentJudges(challengeData.judge_ids.map(jid => ({ id: jid, display_name: jid, username: '' }))); }
        }

        // Check if user has already submitted
        if (user) {
          const userEntry = challengeData.entries?.find(e => e.is_mine === true);
          setHasSubmitted(!!userEntry);

          // Fetch user's stories for submission
          const storiesRes = await api.get('/stories/my?include_drafts=false');
          setMyStories(storiesRes.data.filter(s => !s.challenge_id));
        }
      } catch (error) {
        
        navigate('/challenges');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, api, user, navigate]);

  const handleSubmitEntry = async () => {
    if (!selectedStory) {
      alert('Please select a story to submit');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/challenges/${id}/enter`, {
        story_id: selectedStory,
        challenge_id: id
      });
      setHasSubmitted(true);
      // Refresh challenge data
      const challengeRes = await api.get(`/challenges/${id}?user_id=${user.id}`);
      setChallenge(challengeRes.data);
    } catch (error) {
      
      alert(error.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCleanupEntries = async () => {
    if (!window.confirm('This will remove all fake/phantom entries that were not properly submitted. Continue?')) return;
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const res = await api.post(`/challenges/${id}/entries/cleanup`);
      setCleanupResult(res.data.message);
      // Refresh challenge data
      const challengeRes = await api.get(user ? `/challenges/${id}?user_id=${user.id}` : `/challenges/${id}`);
      setChallenge(challengeRes.data);
    } catch (error) {
      setCleanupResult('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCleaningUp(false);
    }
  };

  const handleJudgeSearch = async (query) => {
    setJudgeSearch(query);
    if (query.length < 1) { setJudgeSearchResults([]); return; }
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(query)}&limit=8`);
      setJudgeSearchResults(res.data.filter(u => !currentJudges.find(j => j.id === u.id)));
    } catch { setJudgeSearchResults([]); }
  };

  const handleAddJudge = async (judgeUser) => {
    const updated = [...currentJudges, { id: judgeUser.id, display_name: judgeUser.display_name, username: judgeUser.username }];
    try {
      await api.put(`/challenges/${id}/judges`, updated.map(j => j.id));
      setCurrentJudges(updated);
      setChallenge(prev => ({ ...prev, judge_ids: updated.map(j => j.id) }));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add judge'); }
    setJudgeSearch('');
    setJudgeSearchResults([]);
  };

  const handleRemoveJudge = async (judgeId) => {
    const updated = currentJudges.filter(j => j.id !== judgeId);
    try {
      await api.put(`/challenges/${id}/judges`, updated.map(j => j.id));
      setCurrentJudges(updated);
      setChallenge(prev => ({ ...prev, judge_ids: updated.map(j => j.id) }));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to remove judge'); }
  };

  const handleAnnounceResults = async () => {
    // Convert awardAssignments: { "category": "entry_id" } to array
    const allowedAwards = challenge.allowed_awards || ['Winner'];
    const awards = Object.entries(awardAssignments)
      .filter(([award, entryId]) => entryId && allowedAwards.includes(award))
      .map(([award, entry_id]) => ({ entry_id, award }));
    setAnnouncing(true);
    try {
      await api.post(`/challenges/${id}/announce-results`, {
        awards,
        announcement_message: announcementMsg
      });
      
      setShowAnnounce(false);
      navigate(`/challenges/${id}/results`);
    } catch (error) {
      
      alert(error.response?.data?.detail || 'Failed to announce results');
    } finally {
      setAnnouncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'judging': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Skeleton className="h-8 w-32 mb-8" />
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-64 w-full mb-8" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!challenge) return null;

  const isActive = challenge.status === 'active';
  const canSubmit = isActive && user && !hasSubmitted && myStories.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="challenge-detail-page">
      {/* Back Button */}
      <Link 
        to="/challenges" 
        className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
        data-testid="back-to-challenges"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Challenges
      </Link>

      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Badge className={getStatusColor(challenge.status)}>
            {challenge.status.charAt(0).toUpperCase() + challenge.status.slice(1)}
          </Badge>
          {challenge.genre_restriction && (
            <span className="text-sm text-text-muted">{challenge.genre_restriction} Only</span>
          )}
        </div>

        <h1 className="font-serif text-4xl font-semibold text-text-primary mb-4">
          {challenge.title}
        </h1>

        <p className="text-lg text-text-secondary mb-6 whitespace-pre-wrap">
          {challenge.description}
        </p>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-text-muted">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Ends: {new Date(challenge.end_date).toLocaleDateString()}
          </span>
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {challenge.entry_count} entries
          </span>
          {challenge.word_limit_max && (
            <span className="flex items-center gap-2">
              Word limit: {challenge.word_limit_min || 0} - {challenge.word_limit_max}
            </span>
          )}
        </div>

        {/* Results / Announce / Judge Panel Buttons */}
        <div className="flex items-center gap-3 mt-6">
          {/* Judge Panel — visible to judges and admins */}
          {user && (user.role === 'admin' || challenge.judge_ids?.includes(user.id)) && (
            <Link to={`/challenges/${id}/judge-panel`}>
              <Button className="gap-2 bg-amber-600 hover:bg-amber-700 text-white" data-testid="judge-panel-btn">
                <Gavel className="w-4 h-4" />
                Judge Panel
              </Button>
            </Link>
          )}
          {/* Admin Cleanup Button */}
          {user?.role === 'admin' && (
            <Button 
              onClick={handleCleanupEntries}
              disabled={cleaningUp}
              className="gap-2 bg-red-600 hover:bg-red-700 text-white"
              data-testid="cleanup-entries-btn"
            >
              <Trash2 className="w-4 h-4" />
              {cleaningUp ? 'Cleaning...' : 'Clean Up Fake Entries'}
            </Button>
          )}
          {cleanupResult && (
            <span className="text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full" data-testid="cleanup-result">
              {cleanupResult}
            </span>
          )}
          {(challenge.status === 'completed' || challenge.status === 'judging') && (
            <Link to={`/challenges/${id}/results`}>
              <Button className="btn-primary gap-2" data-testid="view-results-btn">
                <BarChart3 className="w-4 h-4" />
                View Results
              </Button>
            </Link>
          )}
          {user?.role === 'admin' && (challenge.status === 'completed' || challenge.status === 'judging') && !challenge.results_announced && (
            <Dialog open={showAnnounce} onOpenChange={(open) => {
              setShowAnnounce(open);
              if (open) {
                api.get(`/challenges/${id}/judge-discussion`)
                  .then(res => setJudgeDiscussion(res.data || []))
                  .catch(err => { console.error('Failed to load discussion:', err); setJudgeDiscussion([]); });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="announce-results-btn">
                  <Megaphone className="w-4 h-4" />
                  Announce Results
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">Announce Challenge Results</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-4">
                  {/* Judges said: read-only summary of judge discussion */}
                  {judgeDiscussion.length > 0 && (
                    <div className="bg-brand-accent/5 border border-brand-accent/20 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-text-primary flex items-center gap-2">
                          <Gavel className="w-4 h-4 text-brand-accent" />
                          What the judges said
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const combined = judgeDiscussion.map(m => `${m.author_name}: ${m.content}`).join('\n\n');
                            setAnnouncementMsg(combined);
                          }}
                          data-testid="use-discussion-btn"
                        >
                          Use this
                        </Button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-2 text-sm">
                        {judgeDiscussion.map(m => (
                          <div key={m.id} className="text-text-secondary">
                            <span className="font-medium text-text-primary">{m.author_name}:</span>{' '}
                            <span className="whitespace-pre-wrap">{m.content}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Award assignment per category */}
                  <p className="text-xs text-text-muted" data-testid="announce-valid-entry-count">
                    {challenge.valid_entry_count ?? 0} published {(challenge.valid_entry_count ?? 0) === 1 ? 'entry' : 'entries'}
                    {(challenge.allowed_awards || []).includes('Runner-up') ? ' — Winner and Runner-up can be awarded.' : ' — Winner only (a Runner-up needs five or more entries).'}
                  </p>
                  {(challenge.allowed_awards || ['Winner']).map((category) => (
                    <div key={category}>
                      <label className="text-sm font-medium text-text-primary mb-2 block flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-brand-accent" />
                        {category}
                      </label>
                      <Select 
                        value={awardAssignments[category] || ''} 
                        onValueChange={(val) => setAwardAssignments(prev => ({ ...prev, [category]: val === 'none' ? '' : val }))}
                      >
                        <SelectTrigger data-testid={`award-select-${category}`}>
                          <SelectValue placeholder={category === 'Winner' ? 'Select winner...' : `No ${category}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {category !== 'Winner' && <SelectItem value="none">No {category}</SelectItem>}
                          {challenge.entries?.filter((entry) => entry.story && !entry.story.is_draft
                            && (category === 'Winner' || entry.id !== awardAssignments.Winner)).map((entry) => (
                            <SelectItem key={entry.id} value={entry.id}>
                              {entry.story.title}{entry.story.author_name ? ` — ${entry.story.author_name}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  
                  {/* Announcement message */}
                  <div>
                    <label className="text-sm font-medium text-text-primary mb-2 block">
                      Announcement Message (optional)
                    </label>
                    <Textarea
                      value={announcementMsg}
                      onChange={(e) => setAnnouncementMsg(e.target.value)}
                      placeholder="Congratulations to all participants..."
                      rows={3}
                      className="resize-none"
                      data-testid="announcement-message-input"
                    />
                  </div>

                  <Button 
                    onClick={handleAnnounceResults}
                    disabled={announcing}
                    className="w-full btn-accent"
                    data-testid="confirm-announce-btn"
                  >
                    {announcing ? 'Announcing...' : 'Announce Results'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Cover Image */}
      {challenge.cover_image && (
        <div className="mb-8 rounded-2xl overflow-hidden">
          <img 
            src={challenge.cover_image}
            alt={challenge.title}
            className="w-full h-64 object-cover"
          />
        </div>
      )}

      {/* Writing Prompt */}
      <Card className="mb-8 bg-surface-alt border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-brand-accent" />
            Writing Prompt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-reading text-lg leading-relaxed text-text-primary whitespace-pre-wrap">
            {challenge.prompt}
          </p>
        </CardContent>
      </Card>

      {/* Award Categories */}
      <Card className="mb-8 border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Award className="w-5 h-5 text-brand-tertiary" />
            Awards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary" data-testid="awards-rule-text">{AWARD_RULE_TEXT}</p>
        </CardContent>
      </Card>

      {/* Admin Judge Management */}
      {user?.role === 'admin' && (
        <Card className="mb-8 border-brand-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gavel className="w-5 h-5 text-amber-600" />
              Manage Judges
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Current judges */}
            {currentJudges.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-4">
                {currentJudges.map(j => (
                  <span key={j.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary/10 text-brand-primary rounded-full text-sm">
                    {j.display_name || j.username || j.id}
                    <button onClick={() => handleRemoveJudge(j.id)} className="hover:text-red-500 ml-1" data-testid={`remove-judge-${j.id}`}>&times;</button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted mb-4">No judges assigned.</p>
            )}
            {/* Search to add */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search members to add as judge..."
                value={judgeSearch}
                onChange={(e) => handleJudgeSearch(e.target.value)}
                className="w-full px-3 py-2 border border-brand-primary/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                data-testid="manage-judge-search"
              />
              {judgeSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-brand-primary/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {judgeSearchResults.map(u => (
                    <button key={u.id} onClick={() => handleAddJudge(u)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-alt text-left transition-colors">
                      <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        {(u.display_name || u.username)?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{u.display_name || u.username}</p>
                        <p className="text-xs text-text-muted">@{u.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Judges — only show after results announced */}
      {challenge.judges && challenge.judges.length > 0 && challenge.results_announced && (
        <Card className="mb-8 border-brand-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-brand-primary" />
              Judges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-text-secondary mb-4">
              Here are the judges for this challenge.
            </p>
            <div className="flex flex-wrap gap-4">
              {challenge.judges.map((judge) => (
                <Link 
                  key={judge.id}
                  to={`/writer/${judge.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-primary/5 rounded-full hover:bg-brand-primary/10 transition-colors"
                >
                  <User className="w-4 h-4 text-brand-primary" />
                  <span className="font-medium text-text-primary">{judge.name}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enter Challenge Section */}
      {isActive && (
        <Card className="mb-8 border-brand-accent/30 bg-brand-accent/5" data-testid="challenge-entry-section">
          <CardContent className="p-6">
            {!user ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-brand-accent" />
                  <div>
                    <p className="font-semibold text-text-primary">Want to Enter This Challenge?</p>
                    <p className="text-sm text-text-secondary">Log in or create an account to submit your work.</p>
                  </div>
                </div>
                <Link to="/login">
                  <Button className="btn-accent" data-testid="login-to-enter">Log In to Enter</Button>
                </Link>
              </div>
            ) : hasSubmitted ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-green-600">
                  <CheckCircle className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">Entry Submitted!</p>
                    <p className="text-sm text-green-600/70">Your story has been entered. You can edit or withdraw it below before the deadline.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-brand-accent" />
                  <p className="font-semibold text-text-primary text-lg">Enter This Challenge</p>
                </div>

                {/* Option 1: Write something new */}
                <div className="mb-5 p-4 rounded-xl bg-white border border-brand-primary/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-text-primary">Write a New Entry</p>
                      <p className="text-sm text-text-secondary">Start a fresh piece for this challenge</p>
                    </div>
                    <Link to={`/write?challenge=${id}`}>
                      <Button className="btn-accent gap-2" data-testid="write-for-challenge-btn">
                        <PenLine className="w-4 h-4" />
                        Start Writing
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Option 2: Submit an existing story */}
                <div className="p-4 rounded-xl bg-white border border-brand-primary/10">
                  <p className="font-medium text-text-primary mb-1">Or Submit an Existing Work</p>
                  {myStories.length === 0 ? (
                    <p className="text-sm text-text-muted">No eligible published works available. Write something new above!</p>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3">
                      <Select value={selectedStory} onValueChange={setSelectedStory}>
                        <SelectTrigger className="flex-1" data-testid="story-select-trigger">
                          <SelectValue placeholder="Select a published work..." />
                        </SelectTrigger>
                        <SelectContent>
                          {myStories.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        onClick={handleSubmitEntry}
                        disabled={!selectedStory || submitting}
                        className="btn-primary"
                        data-testid="submit-entry-btn"
                      >
                        {submitting ? 'Submitting...' : 'Submit Entry'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Not Active Message */}
      {!isActive && challenge.status === 'upcoming' && (
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-blue-700">
              <Clock className="w-6 h-6" />
              <div>
                <p className="font-semibold">This Challenge Hasn't Started Yet</p>
                <p className="text-sm text-blue-600">Submissions open {new Date(challenge.start_date).toLocaleDateString()}. Start preparing your entry!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries */}
      <div>
        <h2 className="font-serif text-2xl font-semibold text-text-primary mb-6">
          Entries ({challenge.entry_count || 0})
        </h2>

        {challenge.entries?.length === 0 ? (
          <p className="text-center text-text-muted py-8">
            {user?.role === 'admin' ? 'No entries found.' : 'No entries yet. Be the first to submit!'}
          </p>
        ) : (
          <div className="space-y-4">
            {challenge.entries?.map((entry, idx) => {
              const hasStory = !!entry.story;
              const isMyEntry = entry.is_mine === true;
              const isAdmin = user?.role === 'admin';
              const isBlind = !challenge.results_announced;
              const challengeEnded = new Date().toISOString() > challenge.end_date;

              const handleDeleteEntry = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const title = hasStory ? entry.story.title : 'this entry';
                const action = isAdmin && !isMyEntry ? 'Remove' : 'Withdraw';
                if (!window.confirm(`${action} "${title}" from this challenge?`)) return;
                try {
                  await api.delete(`/challenges/${id}/entries/${entry.id}`);
                  const res = await api.get(`/challenges/${id}?user_id=${user.id}`);
                  setChallenge(res.data);
                  if (isMyEntry) setHasSubmitted(false);
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to delete');
                }
              };

              // During blind judging: show title only, no author, no story link, no cover
              const entryTitle = hasStory ? entry.story.title : `Entry #${idx + 1}`;
              const authorDisplay = isBlind
                ? (isMyEntry ? 'Anonymous (Your Entry)' : 'Anonymous')
                : (hasStory ? entry.story.author_name : 'Unknown Author');

              return (
                <div key={entry.id} className="relative" data-testid={`entry-${entry.id}`}>
                  <Card className={`bg-white border-brand-primary/10 card-hover ${isMyEntry ? 'ring-2 ring-brand-accent/30' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Title: link to story only after results announced */}
                          {hasStory && !isBlind ? (
                            <Link to={`/story/${entry.story_id}`}>
                              <h3 className="font-serif text-lg font-semibold text-text-primary mb-1 hover:text-brand-accent transition-colors">
                                {entryTitle}
                              </h3>
                            </Link>
                          ) : (
                            <h3 className="font-serif text-lg font-semibold text-text-primary mb-1">
                              {entryTitle}
                            </h3>
                          )}
                          <p className="text-sm text-text-muted mb-2">
                            by {authorDisplay}
                          </p>
                          {/* Summary only after results announced */}
                          {hasStory && !isBlind && entry.story.summary && (
                            <p className="text-text-secondary text-sm line-clamp-2">
                              {entry.story.summary}
                            </p>
                          )}
                          {/* Stats only after results announced */}
                          {hasStory && !isBlind && (
                            <div className="flex items-center gap-4 mt-3 text-sm text-text-muted">
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" /> {entry.story.view_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart className="w-4 h-4" /> {entry.story.like_count}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Cover image only after results announced */}
                          {hasStory && !isBlind && entry.story.cover_image && (
                            <img 
                              src={entry.story.cover_image}
                              alt="Entry cover"
                              className="w-24 h-24 rounded-lg object-cover"
                            />
                          )}
                          {/* User controls for own entry before deadline */}
                          {isMyEntry && !challengeEnded && (
                            <>
                              <Link to={`/story/${entry.story_id}/edit`} onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Edit your story"
                                  data-testid={`edit-entry-${entry.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              </Link>
                              <button
                                onClick={handleDeleteEntry}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Withdraw your entry"
                                data-testid={`withdraw-entry-${entry.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {/* Locked indicator after deadline */}
                          {isMyEntry && challengeEnded && (
                            <span className="flex items-center gap-1 text-xs text-text-muted bg-gray-100 px-2 py-1 rounded-full" title="Editing locked after deadline">
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          )}
                          {/* Admin edit (works for any entry, even past deadline) */}
                          {isAdmin && entry.story_id && !(isMyEntry && !challengeEnded) && (
                            <Link to={`/story/${entry.story_id}/edit`} onClick={(e) => e.stopPropagation()}>
                              <button
                                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit this entry (Admin)"
                                data-testid={`admin-edit-entry-${entry.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </Link>
                          )}
                          {/* Admin delete (skip if already showing owner controls) */}
                          {isAdmin && !(isMyEntry && !challengeEnded) && (
                            <button
                              onClick={handleDeleteEntry}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove this entry (Admin)"
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Show awards only after results announced */}
                      {!isBlind && challenge.status === 'completed' && entry.awards?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-brand-primary/10">
                          <div className="flex flex-wrap gap-2">
                            {entry.awards.map((award) => (
                              <AwardBadge key={`award-${award}`} award={award} />
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeDetailPage;
